import { generatePath, useNavigate } from 'react-router-dom';

import { ButtonProps } from '@components/button';
import { IconBuilder } from '@components/icons/builder';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { JourneyListItemType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';

import { useJourneyDetailsSidebar } from './journey-edit-sidebar';
import { useDeleteJourneyModal } from './modals/delete-journey';
import { useDuplicateJourneyModal } from './modals/duplicate-journey';
import { useMoveJourneyModal } from './modals/move';

import { FavoriteMenuItem } from '../../favorites/favorites';

type JourneyMenuProps = {
  journey: JourneyListItemType;
  variant?: ButtonProps['variant'];
};

export const JourneyMenu = ({ journey, variant = 'secondary' }: JourneyMenuProps) => {
  const { t } = useScopedTranslate(locale);

  const showDetailsSidebar = useJourneyDetailsSidebar();
  const showDuplicateJourneyModal = useDuplicateJourneyModal();
  const showDeleteJourneyModal = useDeleteJourneyModal();
  const showMoveJourneyModal = useMoveJourneyModal();

  const navigate = useNavigate();

  return (
    <Menu
      buttonProps={{
        dataCy: 'journey-menu-btn',
        variant,
        iconRight: <IconMoreVertical />,
      }}
    >
      <Menu.Item
        onSelect={() =>
          navigate(
            generatePath(routePaths.editJourney, {
              workspaceId: journey.workspace,
              journeyId: journey.shortId,
              '*': '',
            }),
          )
        }
        label={t('openJourneyBuilder')}
        icon={<IconBuilder />}
      />
      <Menu.Item
        onSelect={() => showDetailsSidebar({ journey })}
        label={t('viewJourneyDetails')}
        icon={<IconInfoSimple />}
      />
      <Menu.Item
        onSelect={() => showDuplicateJourneyModal({ journey })}
        label={t('duplicateJourney')}
        icon={<IconDuplicate />}
      />
      <Menu.Item
        onSelect={() => showMoveJourneyModal({ journey })}
        dataCy="move-journey"
        label={t('moveJourney')}
        icon={<IconMove />}
      />
      <FavoriteMenuItem shortId={journey.shortId} contentType="journey" />
      <Menu.Separator />
      <Menu.Item
        onSelect={() => showDeleteJourneyModal({ journey })}
        dataCy="delete-journey"
        variant="danger"
        label={t('core.delete')}
        icon={<IconTrash />}
      />
    </Menu>
  );
};

export const locale = {
  en: {
    openJourneyBuilder: 'Open builder',
    viewJourneyDetails: 'View journey details',
    duplicateJourney: 'Duplicate journey',
    moveJourney: 'Move to workspace',
  },
};
